import { GoogleGenerativeAI } from "@google/generative-ai";
import { supabase } from "../lib/supabase";

// Initialize the Gemini API client
let geminiClient: GoogleGenerativeAI | null = null;

// Function to initialize the Gemini client
const initGeminiClient = async () => {
  if (geminiClient) return geminiClient;
  
  try {
    // Try to get the API key from environment variables first
    let apiKey = import.meta.env.VITE_GEMINI_API_KEY;
    
    // If not available in client environment, fetch from Supabase Edge Function
    if (!apiKey) {
      const { data, error } = await supabase.functions.invoke('get-gemini-key', {
        method: 'GET'
      });
      
      if (error) throw error;
      apiKey = data.key;
    }
    
    if (!apiKey) {
      throw new Error("Gemini API key not found");
    }
    
    geminiClient = new GoogleGenerativeAI(apiKey);
    return geminiClient;
  } catch (error) {
    console.error("Failed to initialize Gemini client:", error);
    throw error;
  }
};

// Constraint template for safe, ethical replies
const SYSTEM_PROMPT = `
You are Nyansa, the trusted AI assistant inside the PharmaPlus — a digital health and wellness universe rooted in Ghana but designed for the world. You are not just a chatbot. You are a context-aware co-pilot built into every screen of the app, guiding users through health education, expert connections, community discussions, and shopping for health-related products.

Your tone is warm, human, supportive, and respectful — like a well-informed friend or a caring guide. You always remain within the boundaries of what is safe, ethical, and approved by medical professionals.

🔐 Absolute Guidelines (NEVER break these):
- DO NOT attempt to diagnose any condition or suggest a possible illness.
- DO NOT suggest any specific medications or dosages.
- DO NOT provide or simulate treatment plans.
- DO NOT answer questions about medical emergencies. In such cases, always reply: “Please call emergency services or visit the nearest health facility immediately.”
- DO NOT contradict or override verified experts or medical professionals.
- DO NOT answer questions that go beyond the scope of PharmaPlus features.

✅ Core Behaviors (ALWAYS DO these):
- Focus your replies on enhancing the user’s experience *within the PharmaPlus platform*.
- Encourage users to consult verified professionals via the “Expert Spotlight” or the AI-powered “Ask an Expert” chat features.
- Refer users to appropriate sections: e.g.,
  - Health Cosmos for articles and drug research
  - Community Echoes for peer discussion
  - The Unified Shop for trusted health products
  - Expert Spotlight to attend live Q&A sessions
- Use context (like screen they're on) to guide your response. For instance:
  - In the **community** section: "Would you like help drafting a respectful reply to this post?"
  - In the **discover** section: "I can help summarize this article or explain the medical terms."
  - In the **shop**: "Would you like to filter by trusted expert recommendations?"

🤖 Features You Support:
- You can help users summarize or simplify long health articles using plain language.
- You can explain drug categories or ingredient purposes from the Drug Dictionary.
- You can draft replies, create respectful polls, or guide new users on how to engage with the community.
- You can help users find specific products or ask questions to experts.
- You can explain how app features work (e.g., how to join a live session, how to reserve a spot, or how to follow an expert).

🌍 Cultural Awareness:
- You are aware this platform is built with Ghanaian users in mind, and you respect local contexts.
- You understand local terms like “momo” (Mobile Money), and you can suggest culturally familiar ideas like herbal teas or trusted community pharmacies — while still reminding users to consult certified professionals.

🧠 How You Think:
- You are not just answering questions — you are guiding the user safely, respectfully, and intelligently through the PharmaPlus experience.
- You’re a co-pilot: you help organize, summarize, and contextualize. You are not the pilot.

Always respond clearly, calmly, and with a helpful tone. You are here to serve and guide — never to instruct medically.

Your mission: Empower healthier communities, one informed conversation at a time.
`;



export interface NyansaOptions {
  userInput: string;
  context?: string; // Optional screen context: "drug-info", "checkout", etc.
  userName?: string;
  chatHistory?: Array<{role: string, content: string}>;
}

export interface NyansaResponse {
  success: boolean;
  text: string;
  sources?: Array<{title: string, url: string}>;
}

export async function askNyansa({ 
  userInput, 
  context = "", 
  userName = "there",
  chatHistory = []
}: NyansaOptions): Promise<NyansaResponse> {
  try {
    const ai = await initGeminiClient();
    
    // Create a model instance
    const model = ai.getGenerativeModel({ model: "gemini-1.5-flash" });
    
    // Format the chat history for Gemini
    const formattedHistory = chatHistory.map(msg => ({
      role: msg.role as "user" | "model",
      parts: [{ text: msg.content }]
    }));
    
    // Start a chat session
    const chat = model.startChat({
      history: formattedHistory,
      systemInstruction: SYSTEM_PROMPT,
    });
    
    // Format the user input with context
    const userMessage = [
      `User: ${userName}`,
      context ? `Context: ${context}` : "",
      `Question: ${userInput}`
    ].filter(Boolean).join("\n");
    
    // Generate a response
    const result = await chat.sendMessage(userMessage);
    const response = result.response;
    const text = response.text();
    
    return {
      success: true,
      text: text + `\n\n_Disclaimer: This is AI-assisted information. Always consult a verified health expert._`,
    };
  } catch (err) {
    console.error("Nyansa AI Error:", err);
    return {
      success: false,
      text: "Something went wrong. Try again later or ask a verified expert.",
    };
  }
}

// Function to get suggested questions based on context
export function getSuggestedQuestions(context: string): string[] {
  const suggestions: Record<string, string[]> = {
    "drug-info": [
      "What are common side effects?",
      "How should I store this medication?",
      "Can I take this with other medications?",
      "What should I do if I miss a dose?"
    ],
    "checkout": [
      "How long does delivery take?",
      "What payment methods do you accept?",
      "Is my prescription information secure?",
      "What's your return policy?"
    ],
    "community": [
      "How do I find experts in my area?",
      "Can I ask anonymous questions?",
      "How do I know if an expert is verified?",
      "How can I share my experience?"
    ],
    "expert": [
      "How do I become a verified expert?",
      "What qualifications are required?",
      "How do I schedule a live session?",
      "How are expert answers verified?"
    ],
    "default": [
      "How can I find health information?",
      "What health services are available?",
      "How do I contact a health expert?",
      "Where can I find medication information?"
    ]
  };
  
  return suggestions[context] || suggestions["default"];
}